import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { updateShipperUserData } from "../../../../../../../services/settings.services";

const SKELETON_COLS = 3;
const SKELETON_PARENTS = 5;
const SKELETON_CHILDREN = [2, 3, 2, 4, 2];

const Bone = ({ className = "" }) => (
  <div className={`bg-ui-border rounded animate-pulse ${className}`} />
);

const ManagePermissionsSkeleton = () => (
  <div className="overflow-hidden border border-ui-border rounded-xl">
    <table className="border-collapse text-sm w-full">
      <thead>
        <tr className="bg-ui-surface-sunken border-b border-ui-border">
          <th className="px-5 py-3 min-w-[280px] border-r border-ui-border">
            <Bone className="h-3 w-24" />
          </th>
          {Array.from({ length: SKELETON_COLS }).map((_, i) => (
            <th key={i} className="px-5 py-3 min-w-[180px] border-r border-ui-border last:border-r-0">
              <div className="flex flex-col items-center gap-2">
                <Bone className="h-3 w-20" />
                <Bone className="h-6 w-28 rounded-lg" />
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: SKELETON_PARENTS }).map((_, pi) => (
          <>
            <tr key={`sp-${pi}`} className="bg-ui-surface-sunken border-b border-ui-border">
              <td className="px-5 py-2.5 border-r border-ui-border">
                <Bone className="h-3 w-40" />
              </td>
              {Array.from({ length: SKELETON_COLS }).map((_, ci) => (
                <td key={ci} className="px-5 py-2.5 text-center border-r border-ui-border last:border-r-0">
                  <Bone className="h-3.5 w-3.5 rounded mx-auto" />
                </td>
              ))}
            </tr>
            {Array.from({ length: SKELETON_CHILDREN[pi] ?? 2 }).map((_, chi) => (
              <tr key={`sc-${pi}-${chi}`} className="border-b border-ui-surface-sunken">
                <td className="pl-10 pr-5 py-2 border-r border-ui-border">
                  <Bone className="h-2.5 w-32" />
                </td>
                {Array.from({ length: SKELETON_COLS }).map((_, ci) => (
                  <td key={ci} className="px-5 py-2 text-center border-r border-ui-border last:border-r-0">
                    <Bone className="h-3.5 w-3.5 rounded mx-auto" />
                  </td>
                ))}
              </tr>
            ))}
          </>
        ))}
      </tbody>
    </table>
  </div>
);

const ManagePermissions = ({ users, roles, permisions, setUsers, setSnack, isShipper, loading }) => {
  const user = useSelector((state) => state.user);
  const { t } = useTranslation("settings");

  if (loading) return <ManagePermissionsSkeleton />;

  const updateData = async (User, permissions, userRoles) => {
    const saved = await updateShipperUserData({ permissions, roles: userRoles }, User.id);
    if (!saved.error) {
      setSnack((s) => ({ ...s, open: true, text: t("permissions.updated"), severity: "success" }));
    } else {
      setSnack((s) => ({
        ...s,
        open: true,
        text: saved?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    }
  };

  const parentPermissions = async (User, parent, e) => {
    const array = [...users];
    const index = array.findIndex((u) => u.id === User.id);
    const ids = parent.children.map((c) => c.id);
    if (e.target.checked) {
      array[index].permissions = Array.from(new Set([...array[index].permissions, ...ids, parent.id]));
    } else {
      const toRemove = [...ids, parent.id];
      array[index].permissions = array[index].permissions.filter((p) => {
        if (toRemove.includes(p) && array[index].rolesPermissions.includes(p)) return true;
        return !toRemove.includes(p);
      });
    }
    setUsers(array);
    await updateData(User, [...array[index].permissions], [...array[index].roles]);
  };

  const childPermissions = async (User, child, allChildren, e) => {
    const array = [...users];
    const index = array.findIndex((u) => u.id === User.id);
    // Also toggle hidden siblings so the user inherits every sub-permission.
    const hiddenSiblingIds = (allChildren ?? [])
      .filter((c) => c.hidden)
      .map((c) => c.id);
    const idsToToggle = [child.id, ...hiddenSiblingIds];
    if (e.target.checked) {
      array[index].permissions = Array.from(
        new Set([...array[index].permissions, ...idsToToggle])
      );
    } else {
      array[index].permissions = array[index].permissions.filter(
        (p) => !idsToToggle.includes(p)
      );
    }
    setUsers(array);
    await updateData(User, [...array[index].permissions], [...array[index].roles]);
  };

  const assignRoles = async (User, e) => {
    const array = [...users];
    const index = array.findIndex((u) => u.id === User.id);
    const rolePermissions = roles.find((r) => r.id === Number(e.target.value)).permissions;
    array[index].permissions = array[index].permissions.filter((item) => !User.rolesPermissions.includes(item));
    array[index].roles.splice(0, 1);
    array[index].roles.push(Number(e.target.value));
    array[index].permissions = Array.from(new Set([...array[index].permissions, ...rolePermissions]));
    array[index].rolesPermissions = Array.from(new Set([...rolePermissions]));
    setUsers(array);
    const saved = await updateShipperUserData({ roles: [Number(e.target.value)] }, User.id);
    if (!saved.error) {
      setSnack((s) => ({ ...s, open: true, text: t("permissions.roleAssigned"), severity: "success" }));
    } else {
      setSnack((s) => ({
        ...s,
        open: true,
        text: saved?.error?.response?.data?.message ?? t("common:messages.somethingWentWrong"),
        severity: "error",
      }));
    }
  };

  return (
    <div className="overflow-auto border border-ui-border rounded-xl [&::-webkit-scrollbar]:h-1 [&::-webkit-scrollbar-thumb]:bg-ui-border [&::-webkit-scrollbar-thumb:hover]:bg-ui-text-subtle">
      <table className="border-collapse text-sm" style={{ minWidth: "max-content", width: "100%" }}>

        {/* Header */}
        <thead>
          <tr className="bg-ui-surface-sunken border-b border-ui-border">
            <th className="sticky left-0 z-10 bg-ui-surface-sunken text-left px-5 py-3 text-xs font-semibold text-ui-text-muted uppercase tracking-wider min-w-[280px] border-r border-ui-border">
              {t("roles.permissions")}
            </th>
            {users.map((User, userIndex) => (
              <th key={userIndex} className="px-5 py-3 min-w-[180px] text-center border-r border-ui-border last:border-r-0">
                <div className="flex flex-col items-center gap-1.5">
                  <span className="text-xs font-semibold text-ui-text">{User.name}</span>
                  {isShipper ? (
                    <select
                      value={User?.roles[0]?.toString()}
                      onChange={(e) => assignRoles(User, e)}
                      className="text-xs text-ui-text-muted border border-ui-border rounded-lg px-2 py-1 outline-none focus:border-brand focus:ring-1 focus:ring-brand-light bg-ui-surface cursor-pointer transition-all"
                    >
                      {roles.map((role, index) => (
                        <option value={role.id} key={index}>{role.name}</option>
                      ))}
                    </select>
                  ) : (
                    user?.roles?.[0]?.name && (
                      <span className="text-xs text-ui-text-subtle bg-ui-surface-sunken px-2 py-0.5 rounded-full">
                        {user.roles[0].name}
                      </span>
                    )
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        {/* Body */}
        <tbody>
          {permisions?.map((data, index) => (
            <>
              {/* Parent row */}
              <tr key={`parent-${index}`} className="bg-ui-surface-sunken border-b border-ui-border">
                <td className="sticky left-0 z-10 bg-ui-surface-sunken px-5 py-2.5 font-semibold text-ui-text border-r border-ui-border">
                  {data.description}
                </td>
                {users.map((user, userIndex) => {
                  const moduleIds = [
                    data.id,
                    ...(data.children ?? []).map((c) => c.id),
                  ];
                  const checked = moduleIds.some((id) =>
                    user.permissions.includes(id)
                  );
                  const inheritedFromRole = moduleIds.some((id) =>
                    user.rolesPermissions.includes(id)
                  );
                  return (
                    <td key={userIndex} className="px-5 py-2.5 text-center border-r border-ui-border last:border-r-0">
                      <input
                        type="checkbox"
                        className="accent-brand cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={isShipper ? inheritedFromRole : true}
                        checked={checked}
                        onChange={(e) => parentPermissions(user, data, e)}
                      />
                    </td>
                  );
                })}
              </tr>

              {/* Child rows hidden — module-level (parent) checkbox handles all */}
            </>
          ))}
        </tbody>

      </table>
    </div>
  );
};

export default ManagePermissions;
